/**
 * The site.
 *
 * Five documents per locale, each composed here and nowhere else. This file is
 * a table of contents: every page is a list of self-contained sections that
 * read their own copy from the catalogue, so markup never accumulates here.
 *
 * The page is a prop rather than a route. There is no router because there are
 * no client-side navigations to route — each page is a prerendered document
 * with its own `<title>`, its own description and its own OG card, which is the
 * entire reason this build prerenders at all (`scripts/prerender.mjs` says what
 * reads those bytes and never runs a script). A router would give every page
 * after the first the empty head that this page was built to avoid.
 *
 * `LazyMotion` with the `domAnimation` feature set rather than importing the
 * full `motion` bundle: it is about 20 kB smaller, and the one thing this page
 * must not be is slow to arrive on the laptop of somebody deciding whether to
 * buy it.
 */
import { LazyMotion, domAnimation } from 'motion/react'

import { I18nProvider } from './i18n/context'
import type { Locale } from './i18n'
import type { PageId } from './routes'
import { SiteHeader } from './sections/SiteHeader'
import { SiteFooter } from './sections/SiteFooter'
import { HomePage } from './pages/HomePage'
import { ProductPage } from './pages/ProductPage'
import { SecurityPage } from './pages/SecurityPage'
import { CompanyPage } from './pages/CompanyPage'
import { ContactPage } from './pages/ContactPage'
import { GridDrift, ScrollProgress } from './kit'
import './design-system/kit.css'
import './chrome/chrome.css'
import './sections/sections.css'
import './pages/pages.css'

/** The one place a page id becomes markup. A record rather than a switch, so
 *  adding a page to `routes.ts` without building it is a type error here rather
 *  than a blank document at the end of the prerender. */
const PAGES: Record<PageId, () => React.JSX.Element> = {
  home: HomePage,
  product: ProductPage,
  security: SecurityPage,
  company: CompanyPage,
  contact: ContactPage,
}

export function App({ locale, page }: { locale: Locale; page: PageId }) {
  const Page = PAGES[page]
  return (
    <I18nProvider locale={locale}>
      <LazyMotion features={domAnimation} strict>
        <GridDrift />
        <ScrollProgress />
        <SiteHeader page={page} />
        <main id="main" className="kl-main">
          <Page />
        </main>
        <SiteFooter />
      </LazyMotion>
    </I18nProvider>
  )
}
